/**
 * Apply per-tenant branding (primary brand colour + logo) at runtime.
 *
 * We translate the tenant's hex brand colour to HSL so it plugs into the
 * existing `--primary` CSS custom property and everything downstream
 * (buttons, focus rings, badges, borders, etc.) inherits the new colour
 * without component-level changes.
 *
 * If the tenant hasn't configured a colour, we leave the default Praxara
 * trust-blue intact.
 */
export function applyTenantBranding(color?: string | null): void {
  const root = document.documentElement;
  if (!color) {
    root.style.removeProperty('--brand-h');
    root.style.removeProperty('--brand-s');
    root.style.removeProperty('--brand-l');
    return;
  }
  const hsl = hexToHsl(color);
  if (!hsl) return;
  root.style.setProperty('--brand-h', String(hsl.h));
  root.style.setProperty('--brand-s', `${hsl.s}%`);
  root.style.setProperty('--brand-l', `${hsl.l}%`);
}

function hexToHsl(hex: string): { h: number; s: number; l: number } | null {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return null;

  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}
